'use client';

import DefaultIcon from '@/assets/images/Andritz.png';
import { type Product } from '@/domain/products/types';
import { buildSolutionUrl } from '@/utils/string';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { ArrowBackOutlined, CloseOutlined, LaunchOutlined, ShareOutlined } from '@mui/icons-material';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { toast } from 'react-toastify';

interface ContentProps {
  detailedProduct: Product;
  onClose?: () => void;
  isProductDetailPage?: boolean;
}

const headerImageSrc = (product: Product) =>
  product.image?.replace('Image', 'image')?.replace('Icons', 'icons') || DefaultIcon;

const Content = ({ detailedProduct, onClose, isProductDetailPage = false }: ContentProps) => {
  const router = useRouter();
  const handleCopyURL = useCallback(async () => {
    await navigator.clipboard.writeText(`${globalThis.location.origin}${buildSolutionUrl(detailedProduct)}`);
    toast.success('Copied to clipboard!');
  }, [detailedProduct]);

  const handleGoToHome = useCallback(() => {
    router.push('/');
  }, [router]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, md: 2 },
        justifyContent: 'space-between',
        flexShrink: 0,
        p: { xs: 2, md: 4 },
      }}
    >
      {isProductDetailPage && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', alignContent: 'flex-start' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleGoToHome}
            startIcon={<ArrowBackOutlined />}
            color="inherit"
            sx={{ borderColor: 'primary.contrastText', color: 'primary.contrastText' }}
          >
            Home
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, md: 2 },
          flex: '1 1 auto',
          minWidth: 0,
          flexDirection: 'row',
        }}
      >
        <Image
          src={headerImageSrc(detailedProduct)}
          alt={detailedProduct.product}
          width={42}
          height={42}
          style={{ objectFit: 'contain', objectPosition: 'left', flexShrink: 0 }}
        />
        <Typography
          variant="h5"
          color="text.disabled"
          sx={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: 1.2,
            maxWidth: '100%',
          }}
        >
          {detailedProduct?.product}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, md: 2 },
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          color: 'primary.contrastText',
          borderColor: 'primary.contrastText',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <IconButton
          size="small"
          variant="outlined"
          onClick={handleCopyURL}
          color="inherit"
          sx={{ borderColor: 'primary.contrastText' }}
        >
          <ShareOutlined />
        </IconButton>
        {!isProductDetailPage && (
          <>
            <Box>
              <Link
                key={detailedProduct.idProduct}
                target="_blank"
                prefetch={false}
                color="inherit"
                href={buildSolutionUrl(detailedProduct)}
              >
                <IconButton
                  variant="outlined"
                  size="small"
                  color="inherit"
                  sx={{ borderColor: 'primary.contrastText', color: 'primary.contrastText' }}
                >
                  <LaunchOutlined />
                </IconButton>
              </Link>
            </Box>
            {onClose && (
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close product detail"
                sx={{ color: 'primary.contrastText' }}
              >
                <CloseOutlined />
              </IconButton>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Content;
